import React, { useImperativeHandle, useRef, useState } from 'react';
import { styled } from '@ff/ui';
import { useHistory } from 'react-router-dom';
import Box from '@ff/ui/Box';
import Button from '@ff/ui/Button';
import Image from '@ff/ui/Image';
import { ZIndexBuilder } from '@ff/ui/utils/ZIndexBuilder';
import { colors, H1, P13, P14, P16 } from 'client/v3/typography';
import url from 'client/utils/url';
import useConfig from 'client/hooks/useConfig';
import ModelFeatures from 'client/v3/components/ModelFeatures';
import ModelSpecs from 'client/v3/components/ModelSpecs';
import { VehicleSpec } from 'client/hooks/queries/useVehicleModels';
import modelCodes from 'client/constants/modelCode';
import Modal from 'client/v3/components/CustomizedModal';
import Subscribe from './Subscribe';
import useSubscribeFF91 from 'client/hooks/mutations/useSubscribeFF91';
import ActionModal from '@ff/ui/Modal/presets/ActionModal';
import { SubscribeFf91Input } from 'generated-types/graphql';
import modelCode from 'client/constants/modelCode';

export interface SectionHeights {
  name: number;
  specs: number;
  features: number;
  descriptions: number;
}

const showSpecs = false;
const showFeatures = false;

type ModelProps = {
  code: string;
  name: string;
  coverImage: string;
  features: string[];
  specs: VehicleSpec[];
  status: string;
  sectionHeights?: SectionHeights;
  order?: {
    orderNum: string;
    exteriorImage?: string;
    modelCode: string;
    options: { [key: string]: string };
    canUpgradeEdition: boolean;
    canDowngradeEdition: boolean;
  }
}

type Mode = 'Edit' | 'Downgrade' | 'Upgrade' | 'None';

const MODEL_RANK = {
  [modelCodes.FF91Futurist.fullModelCode]: 1,
  [modelCodes.FF91FuturistAlliance.fullModelCode]: 2
};

// TODO: move model description to vehicle s3 asset
const MODEL_DESCRIPTIONS = {
  [modelCodes.FF91Futurist.fullModelCode]: [`The ${modelCodes.FF91Futurist.name} is our class defining 1,050 HP and a revolutionary user experience designed to create a mobile, connected, and luxurious third internet living space.`],
  [modelCodes.FF91FuturistAlliance.fullModelCode]: [
    'Be among the world\'s first to experience the industry-leading 1,050 HP, 0-60 mph in 2.27s. Your preorder makes you part of the exclusive group of futurists to own the vision of tomorrow.',
  ],
  [modelCodes.FF91SNA.fullModelCode]: [
    `Be among the first to experience the class defining ultra-intelligent TechLuxury electric ${modelCodes.FF91SNA.name}. Your preorder makes you part of the exclusive group of futurists to own the vision of tomorrow.`
  ]
}

const zIndices = new ZIndexBuilder({
  headText: {
    carImage: {},
  }
}).zIndices;

function Model(props: ModelProps, ref: React.ForwardedRef<SectionHeights>){
  const history = useHistory();
  const {
    code,
    name,
    coverImage,
    features,
    specs,
    status,
    sectionHeights,
    order
  } = props;

  const qsParams = useConfig();
  const [showSubscribeSuccess, setShowSubscribeSuccess] = useState(false);

  const [subscribeFF91] = useSubscribeFF91();

  let mode: Mode;
  if(order){
    const level = MODEL_RANK[code];
    const currentLevel = MODEL_RANK[order.modelCode];
    if(level === currentLevel){
      mode = 'Edit';
      Object.keys(order.options).forEach(k => {
        qsParams[k] = order.options[k];
      })
    } else if(level > currentLevel){
      mode = 'Upgrade';
    } else{
      mode = 'Downgrade';
    }
  }
  else{
    mode = 'None';
  }

  const openFF91SubscribeByUrl = mode === 'None' && status === 'Subscribe' && qsParams.FF91_SUBSCRIBE === '1';
  const [showSubscribe, setShowSubscribe] = useState(openFF91SubscribeByUrl);
  if(openFF91SubscribeByUrl){
    // remove FF91_SUBSCRIBE from url
    qsParams.FF91_SUBSCRIBE = undefined;
    history.replace(url.getPageNavigationV3('model', qsParams));
  }

  const handleFF91Subscribed = async (data: SubscribeFf91Input) => {
    await subscribeFF91({
      variables: {
        form: data
      }
    });

    setShowSubscribe(false);
    setShowSubscribeSuccess(true);
  };

  // A legacy preorder may not have exterior image
  const imageUrl = mode === 'Edit' ? order?.exteriorImage || coverImage : coverImage;

  const nameRef = useRef<HTMLDivElement>(null);
  const specsRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const descriptionsRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    name: nameRef?.current?.offsetHeight || 0,
    specs: specsRef?.current?.offsetHeight || 0,
    features: featuresRef?.current?.offsetHeight || 0,
    descriptions: descriptionsRef?.current?.offsetHeight || 0,
  }));

  const descriptions = mode === 'None' ? MODEL_DESCRIPTIONS[code] : [];

  const handleButtonClicked = () => {
    if(mode === 'None'){
      if(status === 'Subscribe'){
        setShowSubscribe(true);
        return;
      }
    }

    if(code === modelCodes.FF91SNA.fullModelCode){
      window.location.href = '/us/preorder/ff-91';
      return;
    }

    if(code === modelCodes.FF91Futurist.fullModelCode){
      window.location.href = '/us/preorder/ff-91-Futurist';
      return;
    }

    qsParams['VEH'] = code;
    history.push(url.getPageNavigationV3('options', qsParams));
  }

  const isShown =
    (mode === 'Upgrade' && order?.canUpgradeEdition) ||
    (mode === 'Downgrade' && order?.canDowngradeEdition) ||
    mode === 'Edit' || mode === 'None';

  return (
    <Container isShown={isShown} isEditMode={mode === 'Edit'}>
      <EditModeFlag isEditMode={mode === 'Edit'} />
      <HeadContainer>
        { (mode === 'Upgrade' || mode === 'Downgrade') ? (
          <HeadText>You can choose to <b>{mode.toLocaleLowerCase()}</b> to</HeadText>
        ) : (
          <HeadText />
        )}
        <CarImageContainer>
          <CarImage src={imageUrl} mode={mode} />
        </CarImageContainer>
      </HeadContainer>
        <Box ref={nameRef} height={sectionHeights?.name}>
          <ModelName>
            {name}
          </ModelName>
        </Box>
        <ContainerWithPadding>
          <TextContent>
            { (showSpecs || mode !== 'None') && (
              <SpecsContainer ref={specsRef} height={sectionHeights?.specs} >
                <ModelSpecs specs={specs} />
              </SpecsContainer>
            )}
            { (showFeatures || mode !== 'None') && features?.length > 0 && (
              <FeaturesContainer ref={featuresRef} height={sectionHeights?.features} >
                <ModelFeatures features={features} align="left" />
              </FeaturesContainer>
            )}
            <DescriptionsContainer ref={descriptionsRef} height={sectionHeights?.descriptions}>
              {descriptions.map((des) => (
                <Description key={des}>{des}</Description>
              ))}
            </DescriptionsContainer>
          </TextContent>
          <ButtonContainer>
            <SubscriptionMessage
              isEmpty={code !== 'FF91'}
            >
              {code === 'FF91' ? `Can't wait? Preorder the ${modelCode.FF91Futurist.name} now` : ''}
            </SubscriptionMessage>
            <ActionButton onClick={handleButtonClicked}>
                {mode === 'None' && status === 'Waitlist' && (
                  <ButtonTextLine>Preorder to Join the Waitlist</ButtonTextLine>
                )}
                {/* Alliance: ProductionOrder, Futurist: ProductionOrder, FF91: PreOrder  */}
                {mode === 'None' && (status === 'PreOrder' || status === 'ProductionOrder') && (
                  <ButtonTextLine>Preorder Now</ButtonTextLine>
                )}
                {mode === 'None' && status === 'Subscribe' && (
                  <ButtonTextLine>Subscribe to {modelCode.FF91SNA.name} updates</ButtonTextLine>
                )}
                {mode !== 'None' && (
                  <ButtonTextLine>Configuration</ButtonTextLine>
                )}
            </ActionButton>
          </ButtonContainer>
        </ContainerWithPadding>
        <Modal
          open={showSubscribe}
          onClose={() => setShowSubscribe(false)}
        >
          <Subscribe imageUrl={imageUrl} onSubmit={handleFF91Subscribed}></Subscribe>
        </Modal>
        <ActionModal
          iconName='ff-icon-check_circle_outline'
          title='Thanks for subscribing'
          actionText='Close'
          open={showSubscribeSuccess}
          onAction={() => setShowSubscribeSuccess(false)}
          backdrop
        />
    </Container>
  );
}

const Container = styled(Box)<{ isShown: boolean, isEditMode: boolean }>(({ theme, isShown, isEditMode }) => ({
  display: isShown? 'flex' : 'none',
  flexDirection: 'column',
  alignItems: 'center',
  backgroundColor: theme.colors.white,
  border: colors.border6,
  borderTop: isEditMode ? 'none' : colors.border6,
}));

const EditModeFlag = styled(Box)<{isEditMode: boolean}>(({ theme, isEditMode }) =>({
  alignSelf: 'stretch',
  height: isEditMode ? '5px' : '4px',
  backgroundColor: isEditMode ? theme.colors.black : 'transparent',
}));

const CarImageContainer = styled(Box)({
  overflow: 'hidden',
  maxWidth: '700px',
});
const CarImage = styled(Image)<{mode: Mode}>(({theme, mode}) => ({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  aspectRatio: '16/9',
  transform: 'scale(1.5)',
  alignSelf: 'center',
  margin: '0 auto',
  zIndex: zIndices['carImage'],

  [theme.mediaQueries.lg] : {
    transform: mode === 'Downgrade' || mode === 'Upgrade'? 'scale(1.2)' : 'scale(1.5)',
  }
}));

const HeadText = styled(P16)(({theme}) => ({
  position: 'relative',
  textAlign: 'center',
  height: '20px',
  top: '50px',
  zIndex: zIndices['headText'],

  [theme.mediaQueries.lg]: {
    top: '80px',
  }
}));

const HeadContainer = styled(Box)(({theme, height}) =>({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',

  [theme.mediaQueries.lg]: {
    height: height ? `${height}px` : 'unset',
  }
}));

const SpecsContainer = styled(Box)(({ height, theme }) => ({
  height: 'unset',

  [theme.mediaQueries.lg]: {
    height: height ? `${height}px` : 'unset',
  }

}))

const FeaturesContainer = styled(Box)(({ theme, height }) => ({
  paddingTop: '16px',
  paddingBottom: '16px',
  height: 'unset',

  [theme.mediaQueries.lg]: {
    height: height ? `${height}px` : 'unset',
  }
}))

const TextContent = styled(Box)(({ theme }) => ({
  [theme.mediaQueries.lg]: {
    minHeight: '400px',
  }
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: '28px',
  marginBottom: '40px',
  gap: '16px',

  [theme.mediaQueries.lg]: {
    marginTop: '40px',
    marginBottom: '40px',
  }
}));

const ContainerWithPadding = styled(Box)(({ theme }) => ({
  minWidth: '100%',
  paddingLeft: '16px',
  paddingRight: '16px',

  [theme.mediaQueries.lg]: {
    paddingLeft: '42px',
    paddingRight: '42px',
  }
}))

const ActionButton = styled(Button)(({ theme }) => ({
  height: '38px',
  width: '100%',
  backgroundColor: theme.colors.white,
  border: '1px solid rgba(23, 26, 32, 0.65)',
  borderRadius: '4px',
  backdropFilter: 'blur(4px)',
}));

const ButtonTextLine = styled(P14)({
  color: colors.content1,
  textTransform: 'none',
});

const Description = styled(P14)({
  color: colors.content3,
  lineHeight: '22px',
});

const ModelName = styled(H1)(({ theme, height}) => ({
  color: colors.content1,
  marginBottom: '16px',
  textAlign: 'center',
  height: 'unset',
  padding: '0px 16px',

  [theme.mediaQueries.lg]: {
    height: height ? `${height}px` : 'unset',
  }
}));
ModelName.defaultProps = { as: 'h1' };

const DescriptionsContainer = styled(Box)(({ theme, height }) => ({
  width: '100%',
  paddingTop: '16px',
  borderTop: '0.5px dashed #ACACAC',

  height: 'unset',

  display: 'flex',
  flexDirection: 'column',
  gap: '16px',

  [theme.mediaQueries.lg]: {
    height: height ? `${height}px` : 'unset',
    minHeight: '360px',
  }
}));

const SubscriptionMessage = styled(P13)<{ isEmpty: boolean}>(({ theme, isEmpty }) => ({
  display: isEmpty ? 'none': 'block',

  [theme.mediaQueries.lg]: {
    display: 'block',
    height: '24px',
  },
}))

export default React.forwardRef<SectionHeights, ModelProps>(Model);
